import React, { useEffect } from 'react';
import { Toast } from '../../ui';
import ExtractedBomModal from '../ExtractedBomModal';
import useExtractedBomSave from './useExtractedBomSave';
import { EnhanceSelection } from '../../../utils/bomProposal';

/**
 * The step-2 extracted-BOM sheet wired to the RFQ in the store: lines, BOM id
 * and completeness from Redux, Save through the manual edit, and Robbie's
 * "Saved." toast once the sheet closes. Stays mounted while the sheet is
 * closed so the toast outlives it.
 */
export interface ConnectedExtractedBomModalProps {
  open: boolean;
  onClose: () => void;
  fileName?: string;
  /** A viewer reads the lines; only an editor edits them. */
  readOnly?: boolean;
  /** "Enhance with Robbie" — the caller closes the sheet and asks him. */
  onEnhanceWithRobbie?: (selection?: EnhanceSelection) => void;
}

const NOTICE_MS = 5000;

export const ConnectedExtractedBomModal: React.FC<ConnectedExtractedBomModalProps> = ({
  open,
  onClose,
  fileName,
  readOnly = false,
  onEnhanceWithRobbie,
}) => {
  const sheet = useExtractedBomSave();
  const { notice, dismissNotice } = sheet;

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(dismissNotice, NOTICE_MS);
    return () => clearTimeout(timer);
    // dismissNotice is a fresh closure each render; the notice is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice]);

  return (
    <>
      <ExtractedBomModal
        open={open}
        onClose={onClose}
        components={sheet.components}
        completeness={sheet.completeness}
        fileName={fileName}
        readOnly={readOnly}
        saving={sheet.saving}
        onSave={async (request, changedCount) => {
          const failure = await sheet.save(request, changedCount);
          if (!failure) onClose();
          return failure;
        }}
        onEnhanceWithRobbie={onEnhanceWithRobbie}
      />
      {notice && (
        <div className="fixed bottom-s7 right-s7 z-50 max-md:left-s5 max-md:right-s5">
          <Toast status={notice.status} title={notice.title} message={notice.message} onDismiss={dismissNotice} className="max-md:w-full" />
        </div>
      )}
    </>
  );
};

export default ConnectedExtractedBomModal;
